// This file contains functions for converting the redux state objects into OpenAPI objects
// that can be sent to the backend server for processing.

import Location from "open-api-gen/model/Location.js";
import Trip from "open-api-gen/model/Trip.js";
import Vehicle from "open-api-gen/model/Vehicle";
import VehicleType from "open-api-gen/model/VehicleType.js";
import TravelCost from "open-api-gen/model/TravelCost.js";
import ProblemData from "open-api-gen/model/ProblemData.js";
import CostSettings from "open-api-gen/model/CostSettings.js";
import CostAdder from 'open-api-gen/model/CostAdder.js';
import CostMultiplier from 'open-api-gen/model/CostMultiplier.js';

import {
    durationCalculationNeeded,
    distanceCalculationNeeded
} from 'store/reducers/inputReducer'
import { isCartesian } from 'store/functions/coordinateSystem.js'
import { multipliersOff, resolveCostMethod, travelModel } from 'store/functions/costMethods.js'
import { normalizeTravelCostRows } from 'store/functions/travelCostTyping.js'

/**
 * Converts the locations data from the Redux state into an array of Location instances.
 *
 * @param {Object} state - Redux state containing data and tableSettings.
 * @returns {Location[]} Array of Location instances.
 * @throws {Error} If state or required location data is missing or invalid.
 */
const getLocations = (state) => {
    if (!state) {
        throw new Error('State is not defined');
    }
    const data = state.data;
    if (!data || !data.locations) {
        throw new Error('Data or locations are not defined');
    }
    if (!Array.isArray(data.locations)) {
        throw new Error('Data.locations is not an array');
    }

    // Cartesian scenarios store x/y in the longitude/latitude fields for
    // display only — arbitrary plane values would violate the API's geographic
    // range validation, and the solver never needs coordinates when the
    // travel-cost matrix is fully populated (the planar cost path guarantees it).
    const cartesian = isCartesian(state);

    const locations = data.locations.map(location => Location.constructFromObject({
        locationId: location.UUID,
        ...location,
        ...(cartesian && { latitude: null, longitude: null }),
        demand: state.tableSettings['locations'].isViewable['demand'] ? location.demand : location['demandOff'], //TODO: remove this hack
    }))
    return locations;
}

/**
 * Converts the trips data from the Redux state into an array of Trip instances.
 *
 * @param {Object} state - Redux state containing data and tableSettings.
 * @returns {Trip[]} Array of Trip instances.
 * @throws {Error} If state or required trip data is missing or invalid.
 */
const getTrips = (state) => {
    if (!state) {
        throw new Error('State is not defined');
    }
    const data = state.data;
    if (!data || !data.trips) {
        throw new Error('Data or trips are not defined');
    }
    if (!Array.isArray(data.trips)) {
        throw new Error('Data.trips is not an array');
    }
    // A toggleable column has an `Off` twin holding the value to send when the
    // feature is switched off, so a number the user can no longer see never
    // silently keeps applying.
    const isViewable = state.tableSettings['trips'].isViewable;
    const pairedValue = (column) =>
        isViewable[column] ? trip => trip[column] : trip => trip[`${column}Off`];

    const demandValue = pairedValue('demand');
    const pickupServiceValue = pairedValue('pickupServiceDuration');
    const deliveryServiceValue = pairedValue('deliveryServiceDuration');

    const trips = data.trips.map(trip => Trip.constructFromObject({
        tripId: trip.UUID,
        tripName: trip.UUID,
            ...trip,
        demand: demandValue(trip), //TODO: remove this hack
        pickupServiceDuration: pickupServiceValue(trip),
        deliveryServiceDuration: deliveryServiceValue(trip),
        }));
    return trips;
}

/**
 * Converts the vehicles data from the Redux state into an array of Vehicle instances.
 *
 * @param {Object} state - Redux state containing data.
 * @returns {Vehicle[]} Array of Vehicle instances.
 * @throws {Error} If state or required vehicle data is missing or invalid.
 */
const getVehicles = (state) => {
    if (!state) {
        throw new Error('State is not defined');
    }
    const data = state.data;
    if (!data || !data.vehicles) {
        throw new Error('Data or vehicles are not defined');
    }
    if (!Array.isArray(data.vehicles)) {
        throw new Error('Data.vehicles is not an array');
    }
    const vehicles = data.vehicles.map(vehicle => Vehicle.constructFromObject({
        ...vehicle,
        fixedCost: state.tableSettings['vehicles'].isViewable['fixedCost'] ? vehicle.fixedCost : vehicle.fixedCostOff, //TODO: remove this hack
    }));
    return vehicles;
}

/**
 * Converts the vehicle types data from the Redux state into an array of VehicleType instances.
 *
 * @param {Object} state - Redux state containing data and tableSettings.
 * @returns {VehicleType[]} Array of VehicleType instances.
 * @throws {Error} If state or required vehicle type data is missing or invalid.
 */
const getVehicleTypes = (state) => {
    if (!state) {
        throw new Error('State is not defined');
    }
    const data = state.data;
    if (!data?.vehicle_types) {
        throw new Error('Data or vehicle_types are not defined');
    }
    if (!Array.isArray(data.vehicle_types)) {
        throw new Error('Data.vehicle_types is not an array');
    }
    
    const vehicleTypes = data.vehicle_types.map(vehicleType => VehicleType.constructFromObject({
        vehicleTypeId: vehicleType.UUID,
        ...vehicleType,
        capacity: state.tableSettings['vehicle_types']['isViewable']['capacity'] ? vehicleType.capacity : null, //TODO: remove this hack
        allowResupply: state.tableSettings['vehicle_types'].isViewable['allowResupply'] ? vehicleType.allowResupply : false, //TODO: remove this hack
    }));

    return vehicleTypes;
}

/**
 * Converts the travel costs data from the Redux state into an array of TravelCost instances.
 *
 * @param {Object} state - Redux state containing data.
 * @returns {TravelCost[]} Array of TravelCost instances.
 * @throws {Error} If state or required travel cost data is missing or invalid.
 */
const getTravelCosts = (state) => {
    if (!state) {
        throw new Error('State is not defined');
    }
    const data = state.data;
    if (!data || !data.travel_costs) {
        throw new Error('Data or travel_costs are not defined');
    }
    if (!Array.isArray(data.travel_costs)) {
        throw new Error('Data.travel_costs is not an array');
    }
    /*
    The last gate before the wire, and the only one all three senders pass
    through (/problem, /cost_matrix, and the travel-cost upload). A row that
    names a vehicle type but isn't linked to it is invisible to the solver as
    soon as any other row IS linked — the run then fails outright or drops the
    stops it couldn't reach. Relinking here means no writer, present or future,
    can put that shape on the wire. Silent by design: the store is repaired (and
    the person told) at the load and solve boundaries; this is the backstop.
    */
    const { rows } = normalizeTravelCostRows(data.travel_costs, data.vehicle_types);
    const travelCosts = rows.map(travelCost => TravelCost.constructFromObject({
        ...travelCost
    }));
    return travelCosts;
}

/**
 * Aggregates all relevant data from the Redux state and constructs a ProblemData instance.
 *
 * @param {Object} state - Redux state containing all required data and tableSettings.
 * @returns {ProblemData} The constructed ProblemData instance.
 * @throws {Error} If state or any required data is missing or invalid.
 */
const getProblemData = (state) => {
    if (!state) {
        throw new Error('State is not defined');
    }
    const data = state.data;
    if (!data || !data.locations || !data.trips || !data.vehicles || !data.vehicle_types || !data.travel_costs) {
        throw new Error('Data is not defined or missing required fields');
    }
    const locations = getLocations(state);
    const trips = getTrips(state);
    const vehicles = getVehicles(state);
    const vehicleTypes = getVehicleTypes(state);
    const travelCosts = getTravelCosts(state);

    return ProblemData.constructFromObject({
        "locations": locations,
        "trips": trips,
        "vehicles": vehicles,
        "vehicleTypes": vehicleTypes,
        "travelCosts": travelCosts
    });
}

/*
Never ask for the road network for a quantity that isn't being calculated.

The default durationMethod is 'driving', and it was sent on every /cost_matrix
request, even with duration off (distance metric, no time windows). The backend
treats a request that names 'driving' for either method as a road-network
request: since 2026-08-25 it refuses those to free and signed-out users with 403
PLAN_LIMIT_EXCEEDED, and it also queries the routing engine for them. So with
default settings, "Populate travel costs" failed for every free user, even
though the straight-line distance they needed is open to everyone.

A method for a quantity that isn't calculated has no effect on the numbers, so
send the straight-line default ('geo', which is also the backend's default)
instead. The saved setting is untouched: switch duration on and 'driving' is
requested as before.
*/
const methodForRequest = (method, isCalculated) =>
    (!isCalculated && method === 'driving') ? 'geo' : method;

/*
Travel time is always calculated.

The solver reads cost and travel time for every trip, never distance, and treats
an empty value in either as a trip it cannot make. Duration used to be requested
only when time windows or a time-based objective needed it, so a distance-metric
scenario without time windows came back with every travel time empty, and the run
"completed" with no stops assigned (a signed-in website user, 2026-09-15: 0 of 7).

So every /cost_matrix request asks for travel time. When nothing about the
scenario asks for travel time (no time windows, distance or custom objective), its
method follows the distance method actually requested: straight-line distance
gives straight-line time, driving gives driving. That never adds a road-network
request: a free or signed-out user's distance method has already been downgraded
by resolveCostMethod, and a distance method that isn't calculated is never
'driving' (methodForRequest). When time is configured, its own saved method is
used, as before.
*/

/**
 * What a /cost_matrix request calculates, and with which methods.
 *
 * Shared by the request (getCostSettings), the tags on the calculated rows
 * (fetchTravelCosts) and the Run check's mixed-method notice, so they can't
 * disagree about the method used.
 *
 * @param {Object} state - inputData.present
 * @param {boolean} [roadNetworkAllowed] - see getCostSettings
 * @returns {{ needDistance: boolean, needDuration: true, durationConfigured: boolean,
 *   distanceMethod: string, durationMethod: string }}
 */
const costMatrixRequestMethods = (state, roadNetworkAllowed) => {
    const costs = state.generalSettings.costs;
    const needDistance = distanceCalculationNeeded(state);
    const durationConfigured = durationCalculationNeeded(state);
    const distanceMethod = methodForRequest(resolveCostMethod(costs.distanceMethod, roadNetworkAllowed), needDistance);
    const durationMethod = durationConfigured
        ? resolveCostMethod(costs.durationMethod, roadNetworkAllowed)
        : distanceMethod;
    return { needDistance, needDuration: true, durationConfigured, distanceMethod, durationMethod };
};

/**
 * @param {Object} state - inputData.present.
 * @param {Object} [options]
 * @param {boolean} [options.roadNetworkAllowed] - whether the user may use the
 *   road network (see store/functions/costMethods). When false, 'driving' is
 *   replaced by straight-line for both methods. Omitted means unknown, and the
 *   saved methods are used as they are.
 */
const getCostSettings = (state, options = {}) => {

    if (!state) {
        throw new Error('State is not defined');
    }
    if (!state.generalSettings) {
        throw new Error('State.generalSettings is not defined');
    }
    if (!state.generalSettings.costs) {
        throw new Error('State.generalSettings.costs is not defined');
    }
    const stateCostSettings = state.generalSettings.costs;
    const vehicleTypeDependence = stateCostSettings.vehicleTypeDependence;
    const costAdder = CostAdder.constructFromObject({
        vehicleTypeDependence: false, // TODO: make this dynamic
        baseAdder: 0
    });

    // Costs entered by hand: multipliers are off, as the cost settings say (see
    // multipliersOff). The saved ones stay in state for switching back.
    const manual = multipliersOff(stateCostSettings);
    const vehicleTypeMultipliers = (!vehicleTypeDependence || manual) ? null : stateCostSettings.costMultiplier.vehicleTypes;
    let vehicleTypeMultipliersArray = null;
    if (vehicleTypeMultipliers) {
        const validVehicleTypeIds = new Set(state.data.vehicle_types.map(vt => vt.UUID));
        vehicleTypeMultipliersArray = Object.entries(vehicleTypeMultipliers)
            .filter(([vehicleTypeId]) => validVehicleTypeIds.has(vehicleTypeId))
            .map(([vehicleTypeId, multiplier]) => ({
            vehicleTypeId,
            multiplier
            }));
    }
    const costMultiplier = CostMultiplier.constructFromObject({
            vehicleTypeDependence: vehicleTypeDependence,
            baseMultiplier: manual ? 1 : stateCostSettings.costMultiplier.all,
            vehicleTypeMultipliers: vehicleTypeMultipliersArray
    });

    const request = costMatrixRequestMethods(state, options.roadNetworkAllowed);
    const costSettings = CostSettings.constructFromObject({
        costMetric: stateCostSettings.costMetric,
        costMultiplier: costMultiplier,
        costAdder: costAdder,
        autoCalculateDuration: request.needDuration,
        autoCalculateDistance: request.needDistance,
        distanceMethod: request.distanceMethod,
        durationMethod: request.durationMethod,
        // The scenario's travel model, when it has one (examples publish theirs).
        // Left out otherwise, so the backend's defaults apply as before.
        ...travelModel(stateCostSettings),
    });
    return costSettings;
}

export default getLocations;
export { getLocations, getTrips, getVehicles, getVehicleTypes, getTravelCosts, getProblemData, getCostSettings, costMatrixRequestMethods };
